import React, { useState, useEffect, useCallback } from 'react';
import Project from '../../common/Utils/project';
import { useAuth } from 'oidc-react';
import Pagination from '../../components/Pagination/Pagination';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import api from '../../components/services/apiService';
import { useDebounce } from '../../hooks/useDebounce';
import moment from 'moment';

interface SoftwareAsset {
  id: string;
  name: string;
  version: string;
  installedDate: string;
  agentId: string;
}

interface PaginationInfo {
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
}

interface SoftwareAssetsResponse {
  data: SoftwareAsset[];
  isSuccess: boolean;
  pagination: PaginationInfo;
}

interface Props {
  deviceData?: any;
}

const SoftwareAssets: React.FC<Props> = ({ deviceData }) => {
  const auth = useAuth();
  const [softwareAssets, setSoftwareAssets] = useState<SoftwareAsset[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const itemsPerPage = 20;
  const { selectedTenantId } = useTenantSelection();
  const debouncedSearch = useDebounce(searchTerm, 600);

  const fetchSoftwareAssets = useCallback(
    async (page: number = 1, search: string = '') => {
      setLoadingAssets(true);
      try {
        const token = auth.userData?.access_token;
        const tenantId = selectedTenantId;
        const agentId = deviceData?.agentId || localStorage.getItem('agent_id');

        if (!token || !tenantId || !agentId) return;

        const queryParams = new URLSearchParams({
          pageNumber: page.toString(),
          pageSize: itemsPerPage.toString(),
        });
        if (search.trim()) queryParams.append('search', search.trim());

        const res = await api.get<SoftwareAssetsResponse>(
          `${Project.api}assetmgtservice/SoftwareAssetInventory/${tenantId}/${agentId}/agent-packages?${queryParams}`,
        );

        if (res.data.isSuccess) {
          setSoftwareAssets(res.data.data);
          setPaginationInfo(res.data.pagination);
        }
      } catch (error) {
        console.error('Error fetching software assets:', error);
        setSoftwareAssets([]);
        setPaginationInfo(null);
      } finally {
        setLoadingAssets(false);
      }
    },
    [auth.userData, deviceData?.agentId, itemsPerPage],
  );

  useEffect(() => {
    fetchSoftwareAssets(currentPage, searchTerm);
  }, [fetchSoftwareAssets, currentPage, debouncedSearch]);

  useEffect(() => {
    return () => {
      if (searchTimeout) clearTimeout(searchTimeout);
    };
  }, [searchTimeout]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (searchTimeout) clearTimeout(searchTimeout);

    const timeout = setTimeout(() => {
      setCurrentPage(1);
      fetchSoftwareAssets(1, value);
    }, 500);

    setSearchTimeout(timeout);
  };

  const handlePageChange = (page: number) => setCurrentPage(page);

  const LoadingSkeleton = ({ columnCount }: any) => (
    <>
      {[...Array(itemsPerPage)].map((_, index) => (
        <tr
          key={`skeleton-${index}`}
          className={`${index % 2 === 0 ? 'bg-zinc-900' : 'bg-zinc-800'} animate-slideIn`}
          style={{ animationDelay: `${index * 100}ms`, height: '60px' }}
        >
          {[...Array(columnCount)].map((_, idx) => (
            <td key={idx} className="p-2">
              <div className="animate-pulse bg-zinc-700 h-4 rounded animate-shimmer"></div>
            </td>
          ))}
        </tr>
      ))}
    </>
  );

  return (
    <div>
      {/* Search */}
      <div className="flex mb-4 ml-4 gap-10">
        <div className="relative w-3/5">
          <input
            type="text"
            placeholder="Search software assets..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="bg-zinc-900 text-white px-4 py-2 rounded w-full border border-zinc-700 focus:border-red-500 focus:ring-1 focus:ring-red-500 pr-10"
          />
          {searchTerm && !loadingAssets && (
            <button
              onClick={() => {
                setSearchTerm('');
                fetchSoftwareAssets(1, '');
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors duration-200"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <table className="w-full text-sm table-auto border-collapse ml-4">
        <thead className="bg-zinc-900">
          <tr className="text-left">
            <th className="p-2">Software</th>
            <th className="p-2">Version</th>
            <th className="p-2">Installed Date</th>
          </tr>
        </thead>
        <tbody>
          {loadingAssets ? (
            <LoadingSkeleton columnCount={4} />
          ) : softwareAssets.length === 0 ? (
            <tr>
              <td colSpan={4} className="p-8 text-center text-gray-400">
                No software assets found
              </td>
            </tr>
          ) : (
            softwareAssets.map((item, i) => (
              <tr key={item.id} className={i % 2 === 0 ? 'bg-zinc-900' : 'bg-zinc-800'} style={{ height: '60px' }}>
                <td className="p-2">{item.name}</td>
                <td className="p-2">
                  <span className="px-2 py-1 bg-green-900/30 text-green-400 rounded-md text-sm font-medium">{item.version}</span>
                </td>
                <td className="p-2">
                  {item.installedDate && item.installedDate !== '0001-01-01T00:00:00Z'
                    ? moment(item.installedDate).format('DD MMM YYYY')
                    : '-'}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <Pagination
        currentPage={currentPage}
        totalPages={paginationInfo?.totalPages || 0}
        loading={loadingAssets}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default SoftwareAssets;
